import React, { useEffect } from 'react';
import '../../App.css';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Container from '@mui/material/Container';
import Box from '@mui/material/Box';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { clearState, fetchUserDataByToken, setShowTokenExpiredModal } from '../../app/store/userSlice';
import { Authenticator, View, useAuthenticator } from '@aws-amplify/ui-react';
import { Auth, Hub } from 'aws-amplify';
import '@aws-amplify/ui-react/styles.css';
import { persistSignInSession } from '../../services/auth';
import { sanitizeRedirect } from '../../utils/redirectUtils';
import { USER_TYPES } from '../../services/user';
import { Typography } from '@mui/material';

// Set once the user finishes a password reset, and read on the sign-in that
// follows it. The reset and that sign-in happen on separate screens, so the
// flag has to outlive the route change.
const PASSWORD_RESET_KEY = 'passwordJustReset';

const getTOTPHeader = (title: string, subheader: string) => (
  <>
  <Typography variant="h5" align="center" component="h3" sx={{ fontWeight: 400, mb: 1 }}>
  {title}
  </Typography>
  {subheader && <Box sx={{
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    margin: '25px auto',
    marginBottom: '45px',
    textAlign: 'center',
  }}>
  <p style={{
    position: 'absolute',
    width: '580px',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
  }}>
  {subheader}
  </p>
  </Box>}
  </>
);
export default function Login(props: { userType: string, initialState: 'signIn' | 'signUp' | 'resetPassword' | undefined }) {
  const {
    user,
  } = useAppSelector(state => state.user);
  const dispatch = useAppDispatch();
  const { toSignIn, toSignUp, toResetPassword, route } = useAuthenticator((context) => [context.toSignIn, context.toSignUp, context.toResetPassword, context.route]);
  let envString: string = process.env.REACT_APP_ENVIRONMENT ? ` ${process.env.REACT_APP_ENVIRONMENT}` : '';
  if (envString == ' PROD') envString = ''; // Amplify env vars must have a value set. Remove value from `totpIssuer` string.
  const formFields = {
    setupTOTP: {
      QR: {
        totpIssuer: `Trusted Partner Network${envString}`,
      },
    },
    confirmSignIn: {
      confirmation_code: {
        label: 'Code *',
        placeholder: 'Code',
      },
    },
    signUp: {
      given_name: {
        label: 'First Name',
        placeholder: 'First Name',
        isRequired: true,
        order: 1,
      },
      family_name: {
        label: 'Last Name',
        placeholder: 'Last Name',
        isRequired: true,
        order: 2,
      },
      email: {
        order: 3,
      },
      'custom:phone_number': {
        label: 'Phone',
        placeholder: 'Phone Number',
        isRequired: true,
        order: 4,
      },
      password: {
        order: 5,
      },
      confirm_password: {
        order: 6,
      },
    },
  };

  // MFA is required pool-wide, so every sign-in passes through handleConfirmSignIn.
  // That is the call Cognito answers with RespondToAuthChallenge, and therefore the
  // one that fires the Post Authentication trigger, so the clientMetadata flag has
  // to ride on it rather than on signIn.
  const services = {
    async handleForgotPasswordSubmit({ username, code, password }: { username: string, code: string, password: string }) {
      const result = await Auth.forgotPasswordSubmit(username, code, password);
      sessionStorage.setItem(PASSWORD_RESET_KEY, 'true');
      return result;
    },
    async handleConfirmSignIn({ user: cognitoUser, code, mfaType }: { user: any, code: string, mfaType: any }) {
      const passwordReset = sessionStorage.getItem(PASSWORD_RESET_KEY) === 'true';
      const result = await Auth.confirmSignIn(cognitoUser, code, mfaType, passwordReset ? { password_reset: 'true' } : undefined);
      sessionStorage.removeItem(PASSWORD_RESET_KEY);
      return result;
    },
  };

  useEffect(() => {
    if (props.initialState === 'signIn') toSignIn();
    if (props.initialState === 'signUp') toSignUp();
    if (props.initialState === 'resetPassword') toResetPassword();
  }, [props.initialState]);

  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  useEffect(() => {
    dispatch(setShowTokenExpiredModal(false));

    const listener = async (data: any) => {
      let event = data.payload.event;
      // Robustly persist session-validation state after sign-in (never throws, so a
      // failed token refresh can't skip these writes and force a logout - TPE-2850).
      if (event === 'signIn') {
        await persistSignInSession();
      }

      if (event === 'signIn' || event === 'signUp') {
        localStorage.setItem('fixedLoginTime', Date.now().toString());
        dispatch(fetchUserDataByToken());
      }
    };

    Hub.listen('auth', listener);

    // Cleanup listener when component unmounts or re-renders
    return () => {
      Hub.remove('auth', listener);
    };
  }, [dispatch]);

  useEffect(() => {
    if (route !== 'authenticated' || !user) return;
    dispatch(clearState());

    const redirect = sanitizeRedirect(searchParams.get('redirect'));

    let navTo = '';
    if (user.type !== USER_TYPES.TPN_ADMIN && user.companies.length < 1) {
      navTo  = '/company-onboarding';
    } else if (user.type == USER_TYPES.TPN_ADMIN) {
      navTo = redirect || '/registry';
    } else {
      navTo = redirect || '/profile';
    }
    nav(navTo, { replace: true });
  }, [route, user, nav]);

  return (
    <Container component="main">
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <View className="auth-wrapper">
          <Authenticator
            className="custom-authenticator"
            initialState={props.initialState}
            formFields={formFields}
            services={services}
            components={{
              ConfirmSignUp: {
                Header() {
                  return (
                    <Typography variant="h5" component="h2">
                      Enter Confirmation Code:
                    </Typography>
                  );
                },
              },
              SetupTOTP: {
                Header() {
                  return getTOTPHeader(
                    'Please Confirm One-Time Code',
                    'Open your Authenticator app and scan the QR code below. Tap the &apos;+&apos; symbol to start the scanner. This securely links your TPN+ account for authentication.',
                  );
                },
              },
              ConfirmSignIn: {
                Header() {
                  return getTOTPHeader(
                    'Enter Code From Authenticator App',
                    '',
                  );
                },
              },
            }}
          >
            <input type="hidden" name="custom:userType" value={props.userType} />
          </Authenticator>
        </View>
      </Box>
    </Container>
  );
}

Login.defaultProps = {
  userType: 'vendor',
  initialState: 'signIn',
};
